/**
 * Risk component module.
 * @module shared/components/Risk
 *
 */
import React, {useEffect, useState} from 'react';
import useSWR from 'swr';
import moment from 'moment';
import {Button, DescriptiveText, LoadingSpinner, Row} from '../../../../shared';
import {getRiskPartner, isAuthorized} from '../../../../shared/reducers/user';
import RiskBox from '../../../../shared/components/analyze/RiskBox';
import {getPartnerURI, openRiskPartner} from '../../../utils/partners';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {_openModal} from '../../../../shared/reducers/modals';
import axiosInstance from '../../../../shared/utils/axios';
import {_setNotification} from '../../../../shared/reducers/headerNotification';
import {parseErrorMessage} from '../../../../shared/utils/utils';
import {fixHouseholdType} from '../../../../shared/utils/containerUtils';
import history from '../../../../history';
import qs from 'qs';

const Risk = ({containerID, advisorID, session, context, setNotification}) => {
  const {data: container, mutate: setContainer} = useSWR(containerID && `/containers/${containerID}`);
  const {data: household, mutate: mutateHousehold} = useSWR(
    container
      && fixHouseholdType(container.type) === 'HOUSEHOLD'
      && `/containers/${containerID}/clients?${qs.stringify({riskAdvisorID: advisorID || session.profile.id})}`,
    url => axiosInstance.get(url).then(res => res.data[0])
  );

  const riskPartner = getRiskPartner();

  const [analyzingRisk, setAnalyzingRisk] = useState();
  const [pollingText, setPollingText] = useState('');
  const setRiskPollingTime = (id, riskPollingTime, timeoutID, startTime) => {
    const riskPolling = JSON.parse(localStorage.getItem('riskPolling'));
    const containerRiskPolling = {riskPollingTime, timeoutID, startTime};

    if (!riskPolling) {
      return localStorage.setItem('riskPolling', JSON.stringify({[id]: containerRiskPolling}));
    }

    return localStorage.setItem('riskPolling', JSON.stringify({...riskPolling, [id]: containerRiskPolling}));
  };

  const checkPollingTime = startTime => {
    const stTime = (startTime && (typeof startTime === 'string')) ? moment.utc(startTime, 'YYYY-MM-DDTHH:mm:ss.SSSZ') : startTime;
    const outstandingPollingTime = stTime ? (moment().utc() - stTime) : null;

    if ((outstandingPollingTime !== null) && (outstandingPollingTime < 1920000)) {
      setPollingText('PRISM Rating is in the process of being updated. This may take up to a few minutes.');
    } else if ((outstandingPollingTime >= 1920000) && (outstandingPollingTime <= 18000000)) {
      setPollingText(
        'PRISM Rating is taking longer than expected to update, but we are still working on it. Please check back later.'
      );
    } else if (outstandingPollingTime > 18000000) {
      setPollingText('');

      const riskPolling = JSON.parse(localStorage.getItem('riskPolling'));
      delete riskPolling[containerID];
      localStorage.setItem('riskPolling', JSON.stringify(riskPolling));
    }
  };

  const checkPollingTimeRecursive = () => {
    const riskPolling = JSON.parse(localStorage.getItem('riskPolling'));
    const startTime = (containerID && riskPolling && riskPolling[containerID]) ? riskPolling[containerID].startTime : null;
    checkPollingTime(startTime);

    const timeoutID = setTimeout(checkPollingTimeRecursive, 30000);
    localStorage.setItem('checkPollingTime', String(timeoutID));
  };

  const pollRisk = (riskPollingTime, startTime) => {
    const timeoutID = setTimeout(() => {
      axiosInstance.get(`/containers/${containerID}`)
        .then(res => {
          if (res.data && res.data.riskPollingTime) {
            return pollRisk(res.data.riskPollingTime + 10000, startTime);
          }

          setContainer(current => ({
            ...current,
            riskNumber: res.data.riskNumber,
            riskTarget: res.data.target,
            lastUpdated: res.data.lastUpdated,
            vendorID: res.data.vendorID,
          }));

          setPollingText('');

          const riskPolling = JSON.parse(localStorage.getItem('riskPolling'));
          delete riskPolling[containerID];
          localStorage.setItem('riskPolling', JSON.stringify(riskPolling));
        })
        .catch(err => {
          if (err
                && err.response
                && err.response.data
                && err.response.data.error
                && err.response.data.error.code === 301) {
            return history.push(getPartnerURI(riskPartner));
          }
          setNotification({message: parseErrorMessage(err)});

          const riskPolling = JSON.parse(localStorage.getItem('riskPolling'));
          delete riskPolling[containerID];
          localStorage.setItem('riskPolling', JSON.stringify(riskPolling));
        });
    }, riskPollingTime - moment().utc());

    checkPollingTime(startTime);
    setRiskPollingTime(containerID, riskPollingTime, timeoutID, startTime);
  };

  useEffect(() => {
    checkPollingTimeRecursive();

    return () => {
      const checkPollingTimeTimeoutID = localStorage.getItem('checkPollingTime');

      if (checkPollingTimeTimeoutID) {
        clearTimeout(Number(checkPollingTimeTimeoutID));
      }
    };
  }, []);

  useEffect(() => {
    const riskPolling = JSON.parse(localStorage.getItem('riskPolling'));

    if (containerID && riskPolling && riskPolling[containerID]
        && (!riskPolling[containerID].riskPollingTime || (riskPolling[containerID].riskPollingTime <= moment().utc()))) {
      if (riskPolling[containerID].timeoutID) {
        clearTimeout(riskPolling[containerID].timeoutID);
      }

      delete riskPolling[containerID];
      localStorage.setItem('riskPolling', JSON.stringify(riskPolling));
    }

    const startTime = (containerID && riskPolling && riskPolling[containerID]) ? riskPolling[containerID].startTime : null;
    checkPollingTime(startTime);

    if (container.riskPollingTime && (container.riskPollingTime >= moment().utc())) {
      pollRisk(container.riskPollingTime, startTime);
    } else if (containerID && riskPolling && riskPolling[containerID] && riskPolling[containerID].riskPollingTime
        && (riskPolling[containerID].riskPollingTime >= moment().utc())) {
      pollRisk(riskPolling[containerID].riskPollingTime, startTime);
    }

    return () => {
      const riskPollingCleanup = JSON.parse(localStorage.getItem('riskPolling'));

      if (containerID && riskPollingCleanup && riskPollingCleanup[containerID] && riskPollingCleanup[containerID].timeoutID) {
        clearTimeout(riskPollingCleanup[containerID].timeoutID);
      }

      if (setPollingText) {
        setPollingText('');
      }

      if (setAnalyzingRisk) {
        setAnalyzingRisk(null);
      }
    };
  }, [container]);

  if (!container || (!household && fixHouseholdType(container.type) === 'HOUSEHOLD')) {
    return <LoadingSpinner/>;
  }

  const analyzeRiskButton = (
    <Row>
      <Button importance={'secondary'} size={'md'} label={'Analyze Risk'} processing={analyzingRisk}
              disabled={isAuthorized(['CPLO'])
                    || !container.balance
                    || (riskPartner !== 'stratifi' && advisorID !== session.profile.id)}
              action={() => {
                setAnalyzingRisk(true);
                return axiosInstance.put(`/containers/${containerID}/risk`)
                  .then(res => {
                    setAnalyzingRisk(false);
                    if (riskPartner === 'stratifi') {
                      const riskPolling = JSON.parse(localStorage.getItem('riskPolling'));

                      if (containerID && riskPolling && riskPolling[containerID] && riskPolling[containerID].timeoutID) {
                        clearTimeout(riskPolling[containerID].timeoutID);
                      }

                      setContainer(current => ({...current, vendorID: res.data?.vendorID}));

                      return pollRisk(moment().utc() + 60000, moment().utc());
                    }
                    if (household) {
                      mutateHousehold(`/containers/${containerID}/clients?${qs.stringify({
                        riskAdvisorID: advisorID || session.profile.id,
                      })}`);
                    }
                    if (container.type === 'ACCOUNT') {
                      setContainer(current => ({...current, riskNumber: res.data.accounts[0].risk}));
                    } else {
                      setContainer(current => ({
                        ...current,
                        riskNumber: res.data.risk,
                        riskTarget: res.data.target,
                        lastUpdated: res.data.lastUpdated,
                        vendorID: res.data.vendorID,
                      }));
                    }
                  })
                  .catch(err => {
                    setAnalyzingRisk(false);
                    if (err
                            && err.response
                            && err.response.data
                            && err.response.data.error
                            && err.response.data.error.code === 301) {
                      return history.push(getPartnerURI(riskPartner));
                    }
                    setNotification({message: parseErrorMessage(err)});
                  });
              }}/>
    </Row>
  );

  const lastUpdatedText = (
    <Row>
      <DescriptiveText preset={'standard'}>
        {'Last Updated: ' + (container.lastUpdated
          ? moment.utc(container.lastUpdated, 'YYYY-MM-DD HH:mm:ss').format('M/D/YY')
          : '-')}
      </DescriptiveText>
    </Row>
  );

  let buttonOrText = null;

  if (riskPartner === 'stratifi') {
    if ((context.type === 'ACCOUNT') && (container.riskNumber !== null)) {
      buttonOrText = lastUpdatedText;
    } else if (['ADVISOR_GROUP', 'HOUSEHOLD'].includes(context.type)) {
      buttonOrText = container.vendorID ? lastUpdatedText : analyzeRiskButton;
    }
  } else {
    buttonOrText = analyzeRiskButton;
  }

  return <>
    <Row wrap={true}>
      <RiskBox action={() => openRiskPartner({
        accountID: ((riskPartner !== 'stratifi') && (context.type === 'ACCOUNT')) ? context.id : undefined,
        householdID: ((riskPartner !== 'stratifi') && (fixHouseholdType(context.type) === 'HOUSEHOLD'))
          ? context.id
          : undefined,
        containerID: (riskPartner === 'stratifi') ? context.containerID : undefined,
      })}
               title={'Actual'}
               size={'lg'}
               riskPartner={getRiskPartner()}
               riskNumber={household?.riskNumber || container.riskNumber}
               type={container.type === 'ACCOUNT' ? 'standard' : 'portfolio'}
               isPolling={!!pollingText}/>
      {(household || (riskPartner === 'stratifi' && context.type === 'ADVISOR_GROUP'))
        && <RiskBox action={() => openRiskPartner({
          householdID: ((riskPartner !== 'stratifi') && (fixHouseholdType(context.type) === 'HOUSEHOLD'))
            ? context.id
            : undefined,
          containerID: (riskPartner === 'stratifi') ? context.containerID : undefined,
        })}
                    title={'Target'}
                    size={'lg'}
                    riskPartner={riskPartner}
                    riskNumber={household?.riskTolerance || container.riskTarget}
                    type={'target'}/>}
    </Row>
    {buttonOrText}
    {pollingText
      ? <Row>
        <DescriptiveText preset={'paragraph'}>
          {pollingText}
        </DescriptiveText>
      </Row>
      : null}
  </>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  setNotification: note => dispatch(_setNotification(note)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Risk);

Risk.propTypes = {
  openModal: PropTypes.func,
  setNotification: PropTypes.func,
  containerID: PropTypes.number.isRequired,
  advisorID: PropTypes.number.isRequired,
  /**
   * Context to be forwarded to sso api
   */
  context: PropTypes.object,
  session: PropTypes.object,
};
